import { Injectable } from '@angular/core';
import { ApiService, ApiCacheOptions } from '../../../shared/index';
import { IAccountingPackageService } from './iaccounting-package.service';
import { AccountingPackage } from './accounting-package.model';
import { AccountingPackageRevenueItem } from './accounting-package-revenue-item.model';
import { AccountingPackagePaymentType } from './accounting-package-payment-type.model';
import { AccountingPackageAdjustmentType } from './accounting-package-adjustment-type.model';
import { AccountingPackageMatchOptions } from './accounting-package-match-options.model';
import { AccountDto } from './dto/account.dto';

@Injectable({
  providedIn: 'root',
})
export class AccountingPackageService implements IAccountingPackageService {
  public _apiService: ApiService;
  private _cacheOptions: ApiCacheOptions;
  public constructor(apiService: ApiService) {
    this._apiService = apiService;
    this._cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
  }

  get(accountingPackageId: number): Promise<AccountingPackage> {
    const url = `AccountingPackage/${accountingPackageId}`;
    return this._apiService.get(url);
  }

  create(accountingPackage: AccountingPackage): Promise<number> {
    const url = `AccountingPackage`;
    return this._apiService.post(url, accountingPackage);
  }

  update(accountingPackage: AccountingPackage) {
    const url = `AccountingPackage/${accountingPackage.AccountingPackageId}`;
    return this._apiService.put(url, accountingPackage);
  }

  delete(accountingPackageId: number): Promise<void> {
    const url = `AccountingPackage/${accountingPackageId}`;
    return this._apiService.delete(url);
  }

  public getAccountingPackages = (ignoreCache = false): Promise<Array<AccountingPackage>> => {
    let cacheOptions: ApiCacheOptions;
    if (ignoreCache) {
      cacheOptions = this._apiService.getDefaultApiCacheOptionsForNoCache();
    } else {
      cacheOptions = this._cacheOptions;
    }

    return this._apiService.get('AccountingPackages', cacheOptions);
  };

  public getAccountsForAccountingPackage(id: number, ignoreCache = false): Promise<AccountDto[]> {
    let cacheOptions: ApiCacheOptions;
    if (ignoreCache) {
      cacheOptions = this._apiService.getDefaultApiCacheOptionsForNoCache();
    } else {
      cacheOptions = this._cacheOptions;
    }

    return this._apiService.get(`AccountingPackage/${id}/Accounts`, cacheOptions, true);
  }

  public accountingPackagesMatch = (matchOptions: AccountingPackageMatchOptions): Promise<boolean> => {
    return this._apiService.post('AccountingPackages/Match', matchOptions);
  };

  public getRevenueItemsForAccountingPackage = (accountingPackageId: number): Promise<Array<AccountingPackageRevenueItem>> => {
    return this._apiService.get('AccountingPackage/' + accountingPackageId.toString() + '/RevenueItems', this._cacheOptions);
  };

  public getPaymentTypesForAccountingPackage = (accountingPackageId: number): Promise<Array<AccountingPackagePaymentType>> => {
    return this._apiService.get('AccountingPackage/' + accountingPackageId.toString() + '/PaymentTypes', this._cacheOptions);
  };

  public getAdjustmentTypesForAccountingPackage = (accountingPackageId: number): Promise<Array<AccountingPackageAdjustmentType>> => {
    return this._apiService.get('AccountingPackage/' + accountingPackageId.toString() + '/AdjustmentTypes', this._cacheOptions);
  };

  public getPaymentTypesAvailableForRevenueItem = (itemNum: number): Promise<Array<AccountingPackagePaymentType>> => {
    return this._apiService.get('AccountingPackage/RevenueItem/' + itemNum.toString() + '/PaymentTypes', this._cacheOptions);
  };

  public getPaymentTypesAvailableForInvoiceLineItem = (lineItemId: number): Promise<Array<AccountingPackagePaymentType>> => {
    return this._apiService.get('AccountingPackage/InvoiceLineItem/' + lineItemId.toString() + '/PaymentTypes', this._cacheOptions);
  };
  public getPaymentTypesAvailableForInvoice = (invoiceNum: number): Promise<Array<AccountingPackagePaymentType>> => {
    return this._apiService.get('AccountingPackage/Invoice/' + invoiceNum.toString() + '/PaymentTypes', this._cacheOptions);
  };

  public getRevenueItemsAvailableForPaymentType = (paymentTypeId: number): Promise<Array<AccountingPackageRevenueItem>> => {
    return this._apiService.get('AccountingPackage/PaymentType/' + paymentTypeId.toString() + '/RevenueItems', this._cacheOptions);
  };
  public getRevenueItemsAvailableForInvoicePayment = (paymentId: number): Promise<Array<AccountingPackageRevenueItem>> => {
    return this._apiService.get('AccountingPackage/InvoicePayment/' + paymentId.toString() + '/RevenueItems', this._cacheOptions);
  };

  public getAdjustmentTypesAvailableForPaymentType = (paymentTypeId: number): Promise<Array<AccountingPackageAdjustmentType>> => {
    return this._apiService.get('AccountingPackage/PaymentType/' + paymentTypeId.toString() + '/AdjustmentTypes', this._cacheOptions);
  };
  public getAdjustmentTypesAvailableForRevenueItem = (itemNum: number): Promise<Array<AccountingPackageAdjustmentType>> => {
    return this._apiService.get('AccountingPackage/RevenueItem/' + itemNum.toString() + '/AdjustmentTypes', this._cacheOptions);
  };
  public getAdjustmentTypesAvailableForAdjustmentType = (adjustmentTypeId: number): Promise<Array<AccountingPackageAdjustmentType>> => {
    return this._apiService.get('AccountingPackage/AdjustmentType/' + adjustmentTypeId.toString() + '/AdjustmentTypes', this._cacheOptions);
  };
  public getAdjustmentTypesAvailableForInvoicePayment = (paymentId: number): Promise<Array<AccountingPackageAdjustmentType>> => {
    return this._apiService.get('AccountingPackage/InvoicePayment/' + paymentId.toString() + '/AdjustmentTypes', this._cacheOptions);
  };
  public getAdjustmentTypesAvailableForInvoiceLineItem = (lineItemId: number): Promise<Array<AccountingPackageAdjustmentType>> => {
    return this._apiService.get('AccountingPackage/InvoiceLineItem/' + lineItemId.toString() + '/AdjustmentTypes', this._cacheOptions);
  };
  public getAdjustmentTypesAvailableForAdjustment = (adjustmentId: number): Promise<Array<AccountingPackageAdjustmentType>> => {
    return this._apiService.get('AccountingPackage/Adjustment/' + adjustmentId.toString() + '/AdjustmentTypes', this._cacheOptions);
  };

  public getDefaultPaymentTypeForRevenueItem = (itemNum: number): Promise<Array<AccountingPackagePaymentType>> => {
    return this._apiService.get('AccountingPackage/RevenueItem/' + itemNum.toString() + '/DefaultPaymentType');
  };

  public getDefaultPaymentTypeForInvoiceLineItem = (lineItemId: number): Promise<Array<AccountingPackagePaymentType>> => {
    return this._apiService.get('AccountingPackage/InvoiceLineItem/' + lineItemId.toString() + '/DefaultPaymentType');
  };
}
